import { PayloadAction } from '@reduxjs/toolkit'
import { call, put, SagaReturnType, takeLatest } from 'redux-saga/effects'

import meetingLinkModel, { CreateAvailabilityDto, CreateMeetingLinkDto, UpdateAvailabilityDto } from '@/core/models/meeting-link.model'

import {
  createMeetingLink as createMeetingLinkAction,
  createMeetingLinkFailure as createMeetingLinkFailureAction,
  createMeetingLinkSuccess as createMeetingLinkSuccessAction,
  getMeetingLink as getMeetingLinkAction,
  getMeetingLinkByName as getMeetingLinkByNameAction,
  getMeetingLinkFailure as getMeetingLinkFailureAction,
  getMeetingLinkSuccess as getMeetingLinkSuccessAction,
  getMeetingLinks as getMeetingLinksAction,
  getMeetingLinksFailure as getMeetingLinksFailureAction,
  getMeetingLinksSuccess as getMeetingLinksSuccessAction,
  listMeetingLinks as listMeetingLinksAction,
  listMeetingLinksSuccess as listMeetingLinksSuccessAction,
  listMeetingLinksFailure as listMeetingLinksFailureAction,
  updateMeetingLink as updateMeetingLinkAction,
  updateMeetingLinkSuccess as updateMeetingLinkSuccessAction,
  updateMeetingLinkFailure as updateMeetingLinkFailureAction,
  createAvailability as createAvailabilityAction,
  createAvailabilityFailure as createAvailabilityFailureAction,
  createAvailabilitySuccess as createAvailabilitySuccessAction,
  updateAvailability as updateAvailabilityAction,
  updateAvailabilityFailure as updateAvailabilityFailureAction,
  updateAvailabilitySuccess as updateAvailabilitySuccessAction,
  deleteAvailability as deleteAvailabilityAction,
  deleteAvailabilityFailure as deleteAvailabilityFailureAction,
  deleteAvailabilitySuccess as deleteAvailabilitySuccessAction,
} from '@/redux/reducers/meeting-link.reducer'

import { handleError } from '@/utils/errors'

function* createMeetingLink(action: PayloadAction<CreateMeetingLinkDto>) {
  try {
    const meetingLink: SagaReturnType<typeof meetingLinkModel.createMeetingLink> = yield call(meetingLinkModel.createMeetingLink, action.payload)
    yield put(createMeetingLinkSuccessAction(meetingLink))
  } catch (error) {
    yield put(createMeetingLinkFailureAction(handleError(error)))
  }
}

function* getMeetingLink(action: PayloadAction<string>) {
  try {
    const meetingLink: SagaReturnType<typeof meetingLinkModel.getMeetingLink> = yield call(meetingLinkModel.getMeetingLink, action.payload)
    yield put(getMeetingLinkSuccessAction(meetingLink))
  } catch (error) {
    yield put(getMeetingLinkFailureAction(handleError(error)))
  }
}

function* getMeetingLinkByName(action: PayloadAction<string>) {
  try {
    const meetingLink: SagaReturnType<typeof meetingLinkModel.getMeetingLinkByName> = yield call(meetingLinkModel.getMeetingLinkByName, action.payload)
    if (!!meetingLink) yield put(getMeetingLinkSuccessAction(meetingLink))
    else yield put(getMeetingLinkFailureAction(handleError({ message: 'Not found meeting link' })))
  } catch (error) {
    yield put(getMeetingLinkFailureAction(handleError(error)))
  }
}

function* getMeetingLinks(action: PayloadAction<ReturnType<typeof getMeetingLinksAction>['payload']>) {
  try {
    const meetingLinks: SagaReturnType<typeof meetingLinkModel.getMeetingLinks> = yield call(meetingLinkModel.getMeetingLinks, action.payload)
    yield put(getMeetingLinksSuccessAction(meetingLinks))
  } catch (error) {
    yield put(getMeetingLinksFailureAction(handleError(error)))
  }
}

function* listMeetingLinks(action: PayloadAction<ReturnType<typeof listMeetingLinksAction>['payload']>) {
  try {
    const { query, options } = action.payload
    const { items, totalCount }: SagaReturnType<typeof meetingLinkModel.listMeetingLinks> = yield call(meetingLinkModel.listMeetingLinks, query, options)
    yield put(listMeetingLinksSuccessAction({ items, totalCount }))
  } catch (e) {
    yield put(listMeetingLinksFailureAction(handleError(e)))
  }
}

function* updateMeetingLink(action: PayloadAction<ReturnType<typeof updateMeetingLinkAction>['payload']>) {
  try {
    const { meetingLinkId, updateMeetingLinkDto } = action.payload
    const meetingLink: SagaReturnType<typeof meetingLinkModel.updateMeetingLink> = yield call(
      meetingLinkModel.updateMeetingLink,
      meetingLinkId,
      updateMeetingLinkDto
    )
    yield put(updateMeetingLinkSuccessAction(meetingLink))
  } catch (exception) {
    yield put(updateMeetingLinkFailureAction(handleError(exception)))
  }
}

function* createAvailability(action: PayloadAction<{ meetingId: string; createAvailabilityDto: CreateAvailabilityDto }>) {
  try {
    const meetingLink: SagaReturnType<typeof meetingLinkModel.createAvailability> = yield call(
      meetingLinkModel.createAvailability,
      action.payload.meetingId,
      action.payload.createAvailabilityDto
    )
    yield put(createAvailabilitySuccessAction(meetingLink))
  } catch (error) {
    yield put(createAvailabilityFailureAction(handleError(error)))
  }
}

function* updateAvailability(action: PayloadAction<{ meetingLinkId: string; availabilityId: string; updateAvailabilityDto: UpdateAvailabilityDto }>) {
  try {
    const meetingLink: SagaReturnType<typeof meetingLinkModel.updateAvailability> = yield call(
      meetingLinkModel.updateAvailability,
      action.payload.meetingLinkId,
      action.payload.availabilityId,
      action.payload.updateAvailabilityDto
    )
    yield put(updateAvailabilitySuccessAction(meetingLink))
  } catch (error) {
    yield put(updateAvailabilityFailureAction(handleError(error)))
  }
}

function* deleteAvailability(action: PayloadAction<{ meetingId: string; availabilityId: string }>) {
  try {
    const meetingLink: SagaReturnType<typeof meetingLinkModel.deleteAvailability> = yield call(
      meetingLinkModel.deleteAvailability,
      action.payload.meetingId,
      action.payload.availabilityId
    )
    yield put(deleteAvailabilitySuccessAction(meetingLink))
  } catch (error) {
    yield put(deleteAvailabilityFailureAction(handleError(error)))
  }
}

const meetingsSaga = [
  takeLatest(createMeetingLinkAction.type, createMeetingLink),
  takeLatest(getMeetingLinkAction.type, getMeetingLink),
  takeLatest(getMeetingLinksAction.type, getMeetingLinks),
  takeLatest(getMeetingLinkByNameAction.type, getMeetingLinkByName),
  takeLatest(listMeetingLinksAction.type, listMeetingLinks),
  takeLatest(updateMeetingLinkAction.type, updateMeetingLink),
  takeLatest(createAvailabilityAction.type, createAvailability),
  takeLatest(updateAvailabilityAction.type, updateAvailability),
  takeLatest(deleteAvailabilityAction.type, deleteAvailability),
]

export default meetingsSaga
